/** In-app spectator view, using the same bot as pnpm playtest. */
import { Camera } from '../../src/core/camera'
import markup from './viewer.html?raw'
import './viewer.css'
import type { LevelData } from '../../src/game/types'
import {
  DESIGN_W,
  DESIGN_H,
  applyWorldTransform,
} from '../../src/engine/constants'
import {
  clearHudPopups,
  drawHud,
  drawHudPopups,
  tickHudPopups,
} from '../../src/engine/hud'
import { Renderer3D } from '../../src/engine3d/scene'
import { listCourses, type CourseEntry } from '../../src/levels/library'
import { createPlaytestBot, type PlaytestReport } from './bot'

export function mountPlaytest(
  renderer: Renderer3D,
  world: HTMLCanvasElement,
  hud: HTMLCanvasElement,
  back: () => void,
  draft?: LevelData,
) {
  const root = document.createElement('section')
  root.className = 'playtest-view'
  root.innerHTML = markup
  const worldHome = document.createComment('world')
  const hudHome = document.createComment('hud')
  world.before(worldHome)
  hud.before(hudHome)
  root.querySelector('.stage')!.append(world, hud)
  const host = document.getElementById('stage')!
  const background = [...host.children].filter(
    (node): node is HTMLElement => node instanceof HTMLElement,
  )
  const inertBefore = background.map((node) => node.inert)
  background.forEach((node) => {
    node.inert = true
  })
  host.classList.add('playtesting')
  host.append(root)
  root.addEventListener('keydown', (event) => {
    event.stopPropagation()
    if (event.key === 'Escape') back()
  })
  function element<T extends HTMLElement>(id: string): T {
    const node = root.querySelector('#' + id)
    if (node === null) throw new Error(`Missing playtest control: ${id}`)
    return node as T
  }

  const course = element<HTMLSelectElement>('course')
  const speed = element<HTMLSelectElement>('speed')
  const policy = element<HTMLSelectElement>('policy')
  const pause = element<HTMLButtonElement>('pause')
  const step = element<HTMLButtonElement>('step')
  const status = element('status')
  const metrics = element('metrics')
  const inputs = element('inputs')
  const progress = element<HTMLProgressElement>('progress')
  const deathList = element('deaths')
  const download = element<HTMLButtonElement>('download')
  const tbody = element('results')
  const context = hud.getContext('2d')
  if (context === null) throw new Error('Canvas 2D unavailable')
  const ctx = context
  element('back').textContent = draft ? 'Back to editor' : 'Back to game'
  element('back').addEventListener('click', back)
  element('back').focus()
  const camera = new Camera()

  const entries: (CourseEntry & { draft?: boolean })[] = [...listCourses()]
  let initialIndex = 0
  if (draft) {
    entries.push({ name: draft.name, data: draft, builtIn: false, draft: true })
    initialIndex = entries.length - 1
  }
  const courses = entries.map((entry) => entry.data)
  const rows = courses.map((data, index) => {
    const option = document.createElement('option')
    option.value = String(index)
    option.textContent = `${data.name}${entries[index]?.draft ? ' · Editor draft' : entries[index]?.builtIn ? '' : ' · Saved'}`
    course.append(option)
    const row = document.createElement('tr')
    for (const value of [data.name, 'Not run', '—', '—', '—']) {
      const cell = document.createElement('td')
      cell.textContent = value
      row.append(cell)
    }
    tbody.append(row)
    return row
  })

  type Result = PlaytestReport & {
    outcome: string
    seconds: number
    reach: number
    causes: readonly string[]
  }
  const results = new Map<number, Result>()
  let index = 0
  let bot = createPlaytestBot(courses[0]!)
  let playing = false
  let watchAll = false
  let nextDelay = 0
  let recorded = false
  let seenDeaths = -1
  let hasGoal = true

  function reach(): number {
    const data = bot.level.data
    const goal = data.objects.find((o) => o.id === 'goal')
    if (goal === undefined) return 0
    return Math.round(
      Math.max(
        0,
        Math.min(
          1,
          (bot.report.maxX - data.startX) / Math.max(1, goal.tx - data.startX),
        ),
      ) * 100,
    )
  }

  function load(at: number): void {
    const data = courses[at]
    if (data === undefined) return
    index = at
    course.value = String(at)
    bot = createPlaytestBot(data, { run: policy.value === 'run' })
    const goal = data.objects.find((o) => o.id === 'goal')
    hasGoal = goal !== undefined
    bot.target(goal?.tx ?? data.width - 1, { ty: goal?.ty })
    element('course-note').textContent = !hasGoal
      ? 'No goal placed: the bot will explore toward the right edge, but cannot clear this draft.'
      : entries[at]?.draft
        ? 'Watching a copy of your unsaved editor draft. Back to editor restores your draft and Undo history.'
        : ''
    camera.setBounds(data.width, data.height)
    camera.snapToWorld(bot.player.x, bot.player.y, bot.player.w, bot.player.h)
    clearHudPopups()
    recorded = false
    nextDelay = 0
    seenDeaths = -1
    results.delete(at)
    rows.forEach((row, i) => {
      row.dataset.active = String(i === at)
    })
    updateUi()
  }

  function advance(): void {
    if (bot.finished) return
    const deaths = bot.deaths
    bot.step()
    tickHudPopups()
    const p = bot.player
    if (bot.deaths !== deaths && !bot.finished) {
      camera.snapToWorld(p.x, p.y, p.w, p.h)
      clearHudPopups()
    } else camera.update(p.x, p.y, p.w, p.h, { facing: p.facing, vx: p.vx })
    if (bot.finished && !recorded) {
      recorded = true
      results.set(index, {
        ...bot.report,
        outcome: bot.won
          ? 'Cleared'
          : bot.level.stats.lives <= 0
            ? 'Out of lives'
            : 'Budget reached',
        seconds: +(bot.frame / 60).toFixed(1),
        reach: reach(),
        causes: [...bot.deathCauses],
      })
      if (watchAll && index + 1 < courses.length) nextDelay = 90
      else {
        playing = false
        watchAll = false
      }
    }
  }

  function updateUi(): void {
    const result = results.get(index)
    const state =
      result?.outcome ??
      (playing ? 'Playing' : bot.frame === 0 ? 'Ready' : 'Paused')
    const label = `${bot.level.data.name} · ${state}${nextDelay > 0 ? ' · Next course queued' : ''}`
    if (status.textContent !== label) status.textContent = label
    pause.textContent = playing ? 'Pause' : 'Play'
    pause.disabled = bot.finished && nextDelay === 0
    step.disabled = playing || bot.finished
    download.disabled = results.size === 0
    metrics.textContent = `${(bot.frame / 60).toFixed(1)}s · frame ${bot.frame} · ${bot.deaths} deaths · ${reach()}% horizontal reach`
    const p = bot.player.input
    inputs.textContent = `Bot input: ${[p.left && '←', p.right && '→', p.jump && 'Jump', p.run && 'Run'].filter(Boolean).join(' + ') || 'Wait'}`
    progress.value = reach()
    progress.hidden = !hasGoal
    const row = rows[index]!
    const values = [
      bot.level.data.name,
      state,
      `${(bot.frame / 60).toFixed(1)}s`,
      String(bot.deaths),
      `${reach()}%`,
    ]
    values.forEach((value, i) => {
      row.cells[i]!.textContent = value
    })
    if (seenDeaths !== bot.deaths) {
      seenDeaths = bot.deaths
      deathList.replaceChildren()
      for (const cause of bot.deathCauses.length
        ? bot.deathCauses
        : ['No deaths yet.']) {
        const item = document.createElement('li')
        item.textContent = cause
        deathList.append(item)
      }
    }
  }

  pause.addEventListener('click', () => {
    playing = !playing
    updateUi()
  })
  step.addEventListener('click', () => {
    advance()
    updateUi()
  })
  element('restart').addEventListener('click', () => {
    watchAll = false
    playing = true
    load(index)
  })
  course.addEventListener('change', () => {
    if (!bot.finished && bot.frame > 0)
      rows[index]!.cells[1]!.textContent = 'Stopped'
    watchAll = false
    playing = false
    load(Number(course.value))
  })
  policy.addEventListener('change', () => {
    playing = false
    watchAll = false
    results.clear()
    rows.forEach((row) =>
      ['Not run', '—', '—', '—'].forEach((text, i) => {
        row.cells[i + 1]!.textContent = text
      }),
    )
    element('mode-label').textContent =
      policy.value === 'run' ? '· Run bot' : '· Walk bot'
    load(index)
  })
  element('all').addEventListener('click', () => {
    results.clear()
    rows.forEach((row) =>
      ['Not run', '—', '—', '—'].forEach((text, i) => {
        row.cells[i + 1]!.textContent = text
      }),
    )
    watchAll = true
    playing = true
    load(0)
  })
  download.addEventListener('click', () => {
    const url = URL.createObjectURL(
      new Blob(
        [
          JSON.stringify(
            {
              strategy: policy.value,
              budgetSeconds: 200,
              note: 'Deterministic bot results, not human difficulty ratings. Reach is horizontal distance.',
              results: [...results.values()],
            },
            null,
            2,
          ),
        ],
        { type: 'application/json' },
      ),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = `tavi-playtests-${policy.value}.json`
    document.body.append(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  })

  const onVisibility = () => {
    if (document.hidden) {
      playing = false
      updateUi()
    }
  }
  document.addEventListener('visibilitychange', onVisibility)

  load(initialIndex)
  return {
    update: () => {
      if (!playing) return
      if (nextDelay > 0) {
        if (--nextDelay === 0) load(index + 1)
        return
      }
      for (
        let i = 0;
        i < Number(speed.value) && playing && nextDelay === 0;
        i++
      )
        advance()
    },
    render: () => {
      renderer.setViewport(null)
      renderer.renderLevel(bot.level, camera)
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, DESIGN_W, DESIGN_H)
      applyWorldTransform(ctx)
      drawHudPopups(ctx, camera)
      drawHud(ctx, bot.level, {
        blink: playing,
        world: entries[index]?.draft
          ? 'DRAFT'
          : entries[index]?.builtIn
            ? `1-${index + 1}`
            : 'SAVED',
      })
      updateUi()
    },
    dispose: () => {
      document.removeEventListener('visibilitychange', onVisibility)
      worldHome.replaceWith(world)
      hudHome.replaceWith(hud)
      root.remove()
      host.classList.remove('playtesting')
      background.forEach((node, index) => {
        node.inert = inertBefore[index]!
      })
      clearHudPopups()
    },
  }
}
